import { Box, Container, Typography, Skeleton, Grid } from '@mui/material'
import { CardGiftcard } from '@mui/icons-material'
import { useTranslations } from 'next-intl'

import { Offer } from '@/src/types/offers'

import OfferCardWrapper from '../offerCard'
import { OfferEmptyState } from '../offerEmptyState/offerEmptyState'

interface OffersGridProps {
    offers: Offer[]
    isLoading: boolean
}

const OffersGrid: React.FC<OffersGridProps> = ({ offers, isLoading }) => {
    const t = useTranslations('offers')
    return (
        <Box sx={{ bgcolor: 'grey.50', minHeight: '85vh', py: { xs: 2, sm: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                {/* Header Section */}
                <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mb: { xs: 1.5, md: 2 },
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.5rem' },
                            color: 'textColors.primary',
                            lineHeight: { xs: 1.2, md: 1.3 }
                        }}
                    >
                        {t('allOffersAndDeals')}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'textColors.secondary',
                            mb: { xs: 2, md: 3 },
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '1rem' },
                            px: { xs: 1, sm: 4, md: 0 },
                            maxWidth: { xs: '100%', md: '600px' },
                            mx: 'auto'
                        }}
                    >
                        {t('discoverAmazingDeals')}
                    </Typography>
                </Box>

                {/* Offers Grid */}
                <Grid
                    container
                    spacing={{ xs: 2, sm: 2.5, md: 3 }}
                >
                    {isLoading ? (
                        // Loading Skeletons
                        Array.from({ length: 6 }).map((_, index) => (
                            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }} key={index}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', sm: '100%' },
                                        mx: 'auto'
                                    }}
                                >
                                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
                                    <Box sx={{ pt: 2 }}>
                                        <Skeleton variant="text" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
                                        <Skeleton variant="text" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
                                        <Skeleton
                                            variant="text"
                                            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, width: '60%' }}
                                        />
                                        <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
                                    </Box>
                                </Box>
                            </Grid>
                        ))
                    ) : offers.length > 0 ? (
                        // Offer Cards
                        offers.map(offer => (
                            <Grid
                                size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
                                key={offer.id}
                            >
                                <OfferCardWrapper offer={offer} />
                            </Grid>
                        ))
                    ) : (
                        // No Offers Message - Using OfferEmptyState Component
                        <Grid size={12}>
                            <OfferEmptyState
                                icon={
                                    <CardGiftcard
                                        sx={{
                                            fontSize: { xs: 80, md: 120 },
                                            color: 'textColors.secondary',
                                            opacity: 0.8
                                        }}
                                    />
                                }
                                title={t('noOffersAtTheMoment')}
                                description={t('noOffersDescription')}
                                primaryAction={{
                                    label: t('searchFlights'),
                                    href: '/'
                                }}
                                secondaryAction={{
                                    label: t('browseHotels'),
                                    href: '/'
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Container>
        </Box>
    )
}

export { OffersGrid }
export type { OffersGridProps }
